import React, { useState, useEffect, useRef } from 'react';
import { BanknotesIcon, CreditCardIcon, BuildingLibraryIcon, PlusIcon, TrashIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/authService';

const PaymentInfo = ({ contractData, onChange, selectedVehicle = null, contractNumber = null }) => {
  const [paymentMethod, setPaymentMethod] = useState(contractData.forma_pagamento || 'boleto');
  const [dadosBancarios, setDadosBancarios] = useState(null);
  const [loading, setLoading] = useState(true);
  // Rastreia o último texto auto-gerado para distinguir de edições manuais
  const autoObsRef = useRef('');

  // Buscar dados bancários do Asaas ao montar o componente
  useEffect(() => {
    loadBankInfo();
  }, []);

  const loadBankInfo = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/asaas/account/bank-info');
      if (response.data.success) {
        setDadosBancarios(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados bancários:', error);
      // Fallback para dados padrão em caso de erro
      setDadosBancarios({
        beneficiario: 'ANDRADES NEGOCIOS & SOLUCOES COMERCIAIS LTDA',
        cnpj: '63.786.933/0001-76',
        banco: 'Asaas Pagamentos S.A.',
        agencia: 'Aguardando ativação',
        conta: 'Aguardando ativação',
        chave_pix: '63786933000176'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    onChange({
      ...contractData,
      forma_pagamento: method
    });
  };

  const handleFieldChange = (field, value) => {
    onChange({
      ...contractData,
      [field]: value
    });
  };

  // Gerar observações automáticas quando veículo for selecionado ou alterado
  // Regenera se: (1) campo está vazio, ou (2) campo ainda tem o último texto auto-gerado (não editado manualmente)
  // Preserva texto que o usuário salvou manualmente ao carregar contrato existente
  useEffect(() => {
    if (selectedVehicle) {
      const vehicleDescription = `${selectedVehicle.type || 'Veículo'} ${selectedVehicle.brand || ''} ${selectedVehicle.model || ''}, Placa ${selectedVehicle.license_plate || ''}, Chassi ${selectedVehicle.chassis || ''}`.trim();

      let autoObservations = '';
      if (contractNumber) {
        autoObservations += `Número do Contrato: ${contractNumber}\n`;
      }
      autoObservations += `Descrição do Veículo: ${vehicleDescription}\n`;
      autoObservations += `Boleto será protestado após 5 dias úteis de atraso`;

      // Só atualiza se campo vazio OU se ainda tem o texto auto-gerado anteriormente (não foi editado)
      if (!contractData.observacoes_pagamento || contractData.observacoes_pagamento === autoObsRef.current) {
        autoObsRef.current = autoObservations;
        onChange({
          ...contractData,
          observacoes_pagamento: autoObservations
        });
      }
    }
  }, [contractData.vehicle_id, contractNumber]);

  // Dados bancários padrão da empresa (usa dados do Asaas ou fallback)
  const dadosBancariosPadrao = dadosBancarios || {
    beneficiario: 'Carregando...',
    cnpj: 'Carregando...',
    banco: 'Carregando...',
    agencia: 'Carregando...',
    conta: 'Carregando...',
    chave_pix: 'Carregando...'
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Informações de Pagamento
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Configure a forma de pagamento e vencimento das faturas
        </p>
      </div>

      {/* Pagamento Antecipado */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Pagamento Antecipado</span>
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">Pagar para usar</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              A primeira fatura é gerada ao <strong>aprovar</strong> o contrato, com vencimento antes do início da locação.
              Ideal para contratos onde o equipamento só é entregue após pagamento confirmado.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-1">
            <input
              type="checkbox"
              checked={!!contractData.pagamento_antecipado}
              onChange={(e) => handleFieldChange('pagamento_antecipado', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-orange-500"></div>
          </label>
        </div>

        {contractData.pagamento_antecipado && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                Dias de antecedência:
              </label>
              <input
                type="number"
                min="0"
                max="30"
                value={contractData.dias_antecipacao || 0}
                onChange={(e) => handleFieldChange('dias_antecipacao', e.target.value)}
                className="w-20 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center font-semibold"
              />
              <span className="text-xs text-gray-500">
                {!contractData.dias_antecipacao || parseInt(contractData.dias_antecipacao) === 0
                  ? 'Vence no próprio dia de início'
                  : `Vence ${contractData.dias_antecipacao} dias antes do início`}
              </span>
            </div>
            <div className="mt-2 flex items-start gap-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg p-2.5">
              <svg className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-orange-700 dark:text-orange-300">
                Ao aprovar o contrato, a fatura será gerada automaticamente e integrada ao Asaas para emissão do boleto/PIX.
                Faturas mensais subsequentes também vencerão no próprio mês de competência.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Data de Vencimento */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Dia do vencimento do Boleto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Dia do vencimento do Boleto *
            </label>
            <div className="flex items-center gap-3">
              <span className="text-gray-600 dark:text-gray-400">Dia</span>
              <input
                type="number"
                min="1"
                max="31"
                value={contractData.dia_vencimento_boleto || '10'}
                onChange={(e) => handleFieldChange('dia_vencimento_boleto', e.target.value)}
                className="w-24 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center font-semibold"
              />
              <span className="text-gray-600 dark:text-gray-400">de cada mês</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              As faturas mensais vencerão neste dia
            </p>
          </div>

          {/* Info: Vencimento Automático */}
          <div className={`${contractData.pagamento_antecipado ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'} border rounded-lg p-4`}>
            <div className="flex items-start gap-3">
              <svg className={`w-5 h-5 ${contractData.pagamento_antecipado ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'} mt-0.5 flex-shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className={`text-sm font-medium ${contractData.pagamento_antecipado ? 'text-orange-900 dark:text-orange-200' : 'text-blue-900 dark:text-blue-200'}`}>
                  {contractData.pagamento_antecipado ? 'Pagamento Antecipado' : 'Vencimento Automático'}
                </p>
                <p className={`text-xs ${contractData.pagamento_antecipado ? 'text-orange-700 dark:text-orange-300' : 'text-blue-700 dark:text-blue-300'} mt-1`}>
                  {contractData.pagamento_antecipado
                    ? `1ª fatura gerada ao aprovar o contrato, vencimento ${parseInt(contractData.dias_antecipacao) > 0 ? `${contractData.dias_antecipacao} dias antes do início` : 'no início da locação'}. Mensais vencem no próprio mês.`
                    : 'A primeira fatura vencerá automaticamente 2 dias úteis após a assinatura do locatário. As demais faturas seguirão o dia de vencimento mensal configurado acima.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Forma de Pagamento */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Forma de Pagamento *
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Boleto */}
          <button
            type="button"
            onClick={() => handlePaymentMethodChange('boleto')}
            className={`
              p-4 rounded-lg border-2 transition-all text-left
              ${paymentMethod === 'boleto'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }
            `}
          >
            <div className="flex items-center gap-3 mb-2">
              <BanknotesIcon className={`w-6 h-6 ${paymentMethod === 'boleto' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600'}`} />
              <span className={`font-semibold ${paymentMethod === 'boleto' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                Boleto Bancário
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Boleto gerado mensalmente
            </p>
            {paymentMethod === 'boleto' && (
              <div className="mt-2 px-2 py-1 bg-blue-100 dark:bg-blue-800 rounded text-xs text-blue-700 dark:text-blue-300 font-medium">
                ✓ Selecionado
              </div>
            )}
          </button>

          {/* PIX */}
          <button
            type="button"
            onClick={() => handlePaymentMethodChange('pix')}
            className={`
              p-4 rounded-lg border-2 transition-all text-left
              ${paymentMethod === 'pix'
                ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }
            `}
          >
            <div className="flex items-center gap-3 mb-2">
              <CreditCardIcon className={`w-6 h-6 ${paymentMethod === 'pix' ? 'text-green-600 dark:text-green-400' : 'text-gray-600'}`} />
              <span className={`font-semibold ${paymentMethod === 'pix' ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'}`}>
                PIX
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Pagamento instantâneo
            </p>
            {paymentMethod === 'pix' && (
              <div className="mt-2 px-2 py-1 bg-green-100 dark:bg-green-800 rounded text-xs text-green-700 dark:text-green-300 font-medium">
                ✓ Selecionado
              </div>
            )}
          </button>

          {/* Transferência */}
          <button
            type="button"
            onClick={() => handlePaymentMethodChange('transferencia')}
            className={`
              p-4 rounded-lg border-2 transition-all text-left
              ${paymentMethod === 'transferencia'
                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }
            `}
          >
            <div className="flex items-center gap-3 mb-2">
              <BuildingLibraryIcon className={`w-6 h-6 ${paymentMethod === 'transferencia' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-600'}`} />
              <span className={`font-semibold ${paymentMethod === 'transferencia' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-900 dark:text-white'}`}>
                Transferência
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              TED/DOC bancário
            </p>
            {paymentMethod === 'transferencia' && (
              <div className="mt-2 px-2 py-1 bg-purple-100 dark:bg-purple-800 rounded text-xs text-purple-700 dark:text-purple-300 font-medium">
                ✓ Selecionado
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Dados Bancários - Boleto */}
      {paymentMethod === 'boleto' && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <BanknotesIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h4 className="font-semibold text-gray-900 dark:text-white">
              Informações do Boleto
            </h4>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Beneficiário:</span>
              <span className="font-medium text-gray-900 dark:text-white">{dadosBancariosPadrao.titular}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">CNPJ:</span>
              <span className="font-medium text-gray-900 dark:text-white">{dadosBancariosPadrao.cnpj}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Banco:</span>
              <span className="font-medium text-gray-900 dark:text-white">{dadosBancariosPadrao.banco}</span>
            </div>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-3">
              📄 Boleto será gerado e enviado mensalmente para o email do cliente
            </p>
          </div>
        </div>
      )}

      {/* Dados Bancários - PIX */}
      {paymentMethod === 'pix' && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <CreditCardIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
            <h4 className="font-semibold text-gray-900 dark:text-white">
              Chave PIX
            </h4>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Tipo de Chave:
              </label>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">CNPJ</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Chave PIX:
              </label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-lg font-mono font-bold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-3 py-2 rounded">
                  {dadosBancariosPadrao.chave_pix}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(dadosBancariosPadrao.chave_pix);
                    alert('Chave PIX copiada!');
                  }}
                  className="px-3 py-2 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                >
                  Copiar
                </button>
              </div>
            </div>
            <div className="mt-3 space-y-1 text-xs">
              <p className="text-gray-600 dark:text-gray-400">
                <strong>Favorecido:</strong> {dadosBancariosPadrao.titular}
              </p>
              <p className="text-gray-600 dark:text-gray-400">
                <strong>Banco:</strong> {dadosBancariosPadrao.banco}
              </p>
            </div>
            <p className="text-xs text-green-600 dark:text-green-400 mt-3">
              💳 Cliente pode pagar usando esta chave PIX
            </p>
          </div>
        </div>
      )}

      {/* Dados Bancários - Transferência */}
      {paymentMethod === 'transferencia' && (
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <BuildingLibraryIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <h4 className="font-semibold text-gray-900 dark:text-white">
              Dados Bancários para Transferência
            </h4>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Banco:
                </label>
                <p className="font-semibold text-gray-900 dark:text-white">{dadosBancariosPadrao.banco}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Tipo de Conta:
                </label>
                <p className="font-semibold text-gray-900 dark:text-white">{dadosBancariosPadrao.tipo_conta}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Agência:
                </label>
                <p className="font-mono font-semibold text-gray-900 dark:text-white">{dadosBancariosPadrao.agencia}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Conta:
                </label>
                <p className="font-mono font-semibold text-gray-900 dark:text-white">{dadosBancariosPadrao.conta}</p>
              </div>
            </div>
            <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Titular:
                </label>
                <p className="font-semibold text-gray-900 dark:text-white">{dadosBancariosPadrao.titular}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  CNPJ:
                </label>
                <p className="font-mono font-semibold text-gray-900 dark:text-white">{dadosBancariosPadrao.cnpj}</p>
              </div>
            </div>
            <p className="text-xs text-purple-600 dark:text-purple-400 mt-3">
              🏦 Cliente deve realizar TED/DOC para esta conta
            </p>
          </div>
        </div>
      )}

      {/* Testemunhas */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <UserGroupIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <h4 className="font-semibold text-gray-900 dark:text-white">Testemunhas</h4>
            <span className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 px-2 py-0.5 rounded-full font-medium">
              {(contractData.testemunhas || []).length}
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              const testemunhas = [...(contractData.testemunhas || []), { nome: '', cpf: '', email: '' }];
              onChange({ ...contractData, testemunhas });
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-600 hover:text-purple-700 bg-purple-50 hover:bg-purple-100 dark:bg-purple-900/20 dark:hover:bg-purple-900/30 dark:text-purple-400 rounded-lg transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Adicionar
          </button>
        </div>
        <div className="space-y-3">
          {(contractData.testemunhas || []).map((t, idx) => (
            <div key={idx} className="flex items-start gap-3 bg-gray-50 dark:bg-gray-900/30 rounded-lg p-3">
              <span className="mt-2 text-xs font-bold text-purple-600 dark:text-purple-400 whitespace-nowrap">
                {idx + 1}.
              </span>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                <input
                  type="text"
                  value={t.nome}
                  onChange={(e) => {
                    const testemunhas = [...contractData.testemunhas];
                    testemunhas[idx] = { ...testemunhas[idx], nome: e.target.value };
                    onChange({ ...contractData, testemunhas });
                  }}
                  placeholder="Nome completo"
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
                <input
                  type="text"
                  value={t.cpf}
                  onChange={(e) => {
                    const testemunhas = [...contractData.testemunhas];
                    testemunhas[idx] = { ...testemunhas[idx], cpf: e.target.value };
                    onChange({ ...contractData, testemunhas });
                  }}
                  placeholder="CPF"
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
                <input
                  type="email"
                  value={t.email}
                  onChange={(e) => {
                    const testemunhas = [...contractData.testemunhas];
                    testemunhas[idx] = { ...testemunhas[idx], email: e.target.value };
                    onChange({ ...contractData, testemunhas });
                  }}
                  placeholder="Email"
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const testemunhas = contractData.testemunhas.filter((_, i) => i !== idx);
                  onChange({ ...contractData, testemunhas });
                }}
                className="mt-1 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                title="Remover testemunha"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
          {(!contractData.testemunhas || contractData.testemunhas.length === 0) && (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
              Nenhuma testemunha adicionada. Clique em "Adicionar" acima.
            </p>
          )}
        </div>
      </div>

      {/* Observações sobre pagamento */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Observações sobre Pagamento (opcional)
        </label>
        <textarea
          value={contractData.observacoes_pagamento || ''}
          onChange={(e) => handleFieldChange('observacoes_pagamento', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          rows={3}
          placeholder="Ex: Pagamento deve ser confirmado via email, multa de 2% após vencimento, etc."
        />
      </div>
    </div>
  );
};

export default PaymentInfo;
